import { useState, useEffect, useMemo } from 'react';
import { fuzzyFilter } from '@practium/shared';
import { useGetBindingUser, useGetBindingUsersStats } from '@practium/features/api';
import { mapListPageLineChart, mapListPagePieData } from '@practium/features/dashboard';
import { useForBindingId } from '../../forBinding';
import type { FilterGroup } from '@practium/ui';
import type { BindingUser, BindingTab, BindingUsersPermissions } from '../types';
import { authStorage } from '@practium/features/auth';

const PAGE_SIZE = 10;
const API_LIMIT = 100;

const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Status',
		options: [
			{ value: 'status:Ativo', label: 'Ativo' },
			{ value: 'status:Inativo', label: 'Inativo' },
		],
	},
];

export interface UseBindingUsersPageProps {
	permissions: BindingUsersPermissions;
	initialTab?: BindingTab;
}

export const useBindingUsersPage = ({
	permissions,
	initialTab = 'farmers',
}: UseBindingUsersPageProps) => {
	const [activeTab, setActiveTab] = useState<BindingTab>(initialTab);
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState(false);
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [page, setPage] = useState(1);
	const user = authStorage.getUser();
	const forBindingId = useForBindingId();
	const effectiveUserId = forBindingId ?? user?._id;

	const farmersQuery = useGetBindingUser<BindingUser>({
		bindingType: 1,
		forBinding: effectiveUserId,
		limit: API_LIMIT,
		enabled: permissions.canManageFarmers,
	});

	const consultantsQuery = useGetBindingUser<BindingUser>({
		bindingType: 2,
		forBinding: effectiveUserId,
		limit: API_LIMIT,
		enabled: permissions.canManageConsultants,
	});

	const { data: chartsData, isLoading: isChartsLoading } = useGetBindingUsersStats(
		permissions.canManageFarmers || permissions.canManageConsultants
	);

	const farmers = farmersQuery.data;
	const consultants = consultantsQuery.data;
	const isLoading =
		(permissions.canManageFarmers && farmersQuery.isLoading) ||
		(permissions.canManageConsultants && consultantsQuery.isLoading) ||
		isChartsLoading;

	useEffect(() => {
		setPage(1);
	}, [searchQuery, filterValues]);

	const handleTabChange = (tab: BindingTab) => {
		setActiveTab(tab);
		setSearchQuery('');
		setFilterValues([]);
		setPage(1);
	};

	const statusFilters = useMemo(
		() => filterValues.filter((v) => v.startsWith('status:')).map((v) => v.slice(7)),
		[filterValues]
	);

	const toDisplayStatus = (active: boolean) => (active ? 'Ativo' : 'Inativo');

	const processedFarmers = useMemo(() => {
		let result = fuzzyFilter(farmers, searchQuery, (f) =>
			[f.name, f.surName, f.phoneNumber ?? '', f.email, toDisplayStatus(f.status)]
				.filter(Boolean)
				.join(' ')
		);
		if (statusFilters.length > 0)
			result = result.filter((f) => statusFilters.includes(toDisplayStatus(f.status)));
		if (sortAsc) result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		return result;
	}, [farmers, searchQuery, statusFilters, sortAsc]);

	const processedConsultants = useMemo(() => {
		let result = fuzzyFilter(consultants, searchQuery, (c) =>
			[c.name, c.surName, c.phoneNumber ?? '', c.email, toDisplayStatus(c.status)]
				.filter(Boolean)
				.join(' ')
		);
		if (statusFilters.length > 0)
			result = result.filter((c) => statusFilters.includes(toDisplayStatus(c.status)));
		if (sortAsc) result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		return result;
	}, [consultants, searchQuery, statusFilters, sortAsc]);

	const totalPages = useMemo(() => {
		const len = activeTab === 'consultants' ? processedConsultants.length : processedFarmers.length;
		return Math.max(1, Math.ceil(len / PAGE_SIZE));
	}, [activeTab, processedFarmers.length, processedConsultants.length]);

	const filteredFarmers = useMemo(
		() => processedFarmers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedFarmers, page]
	);

	const filteredConsultants = useMemo(
		() => processedConsultants.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedConsultants, page]
	);

	const profilesPieData = useMemo(() => {
		if (permissions.canManageConsultants) {
			return mapListPagePieData(chartsData?.pie?.items);
		}
		return mapListPagePieData(chartsData?.statusPie?.items);
	}, [chartsData, permissions.canManageConsultants]);

	const {
		lineData: insertionsLineData,
		mainValue: chartMainValue,
		trendValue: chartTrendValue,
		trendDirection: chartTrendDirection,
		footerText: insertionsFooterText,
	} = useMemo(() => mapListPageLineChart(chartsData?.line), [chartsData]);

	return {
		activeTab,
		setActiveTab: handleTabChange,
		farmers,
		consultants,
		filteredFarmers,
		filteredConsultants,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort: () => setSortAsc((v) => !v),
		filterValues,
		setFilterValues,
		filterGroups: FILTER_GROUPS,
		page,
		setPage,
		totalPages,
		isLoading,
		permissions,
		profilesPieData,
		insertionsLineData,
		chartMainValue,
		chartTrendValue,
		chartTrendDirection,
		insertionsFooterText,
	};
};
